import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CancelPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading"); // loading, success, error, already
  const [eventTitle, setEventTitle] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) {
      setStatus("error");
      setErrorMsg("Kein Stornierungstoken vorhanden.");
      return;
    }

    const cancelRegistration = async () => {
      try {
        const { data } = await axios.post(`${API}/registrations/cancel`, { token });
        setEventTitle(data.event_title || "");
        setStatus("success");
      } catch (err) {
        const detail = err.response?.data?.detail;
        if (typeof detail === "string" && detail.includes("bereits storniert")) {
          setStatus("already");
        } else {
          setStatus("error");
          setErrorMsg(typeof detail === "string" ? detail : "Stornierung fehlgeschlagen.");
        }
      }
    };

    cancelRegistration();
  }, [token]);

  return (
    <div data-testid="cancel-page" className="min-h-screen bg-[#0C1620] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <img src="/images/vmbs-logo.png" alt="VMBS" className="h-16 w-16 mx-auto mb-8 object-contain" />

        {status === "loading" && (
          <div data-testid="cancel-loading">
            <Loader2 className="w-12 h-12 text-[#C5993A] mx-auto mb-4 animate-spin" />
            <p className="text-[#8899AA]">Stornierung wird verarbeitet...</p>
          </div>
        )}

        {status === "success" && (
          <div data-testid="cancel-success">
            <CheckCircle className="w-16 h-16 text-[#C5993A] mx-auto mb-6" />
            <h1 className="font-heading text-3xl text-white mb-3">Stornierung erfolgreich</h1>
            <p className="text-[#8899AA] mb-2">
              Ihre Anmeldung zur Veranstaltung{eventTitle && <> <strong className="text-white">„{eventTitle}"</strong></>} wurde storniert.
            </p>
            <p className="text-[#3A4F60] text-sm mb-8">Eine Benachrichtigung wurde an den Veranstalter gesendet.</p>
            <Link to="/">
              <Button className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        )}

        {status === "already" && (
          <div data-testid="cancel-already">
            <XCircle className="w-16 h-16 text-[#8899AA] mx-auto mb-6" />
            <h1 className="font-heading text-3xl text-white mb-3">Bereits storniert</h1>
            <p className="text-[#8899AA] mb-8">Diese Anmeldung wurde bereits storniert.</p>
            <Link to="/">
              <Button className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        )}

        {status === "error" && (
          <div data-testid="cancel-error">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-6" />
            <h1 className="font-heading text-3xl text-white mb-3">Fehler</h1>
            <p className="text-[#8899AA] mb-8">{errorMsg}</p>
            <Link to="/">
              <Button className="bg-gradient-to-r from-[#C5993A] to-[#8B6B20] text-white border-0">
                Zurück zur Startseite
              </Button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
